import * as R from 'ramda';
import { put, call, select, takeLatest } from 'redux-saga/effects';
// components
import { openLoader, closeLoader } from '../../components/loader/actions';
import { transformSearchCriteriaBeforeReportPost } from '../../components/edit-report/helpers';
// features
import { makeSelectCurrentBranchGuid } from '../branch/selectors';
import { getAllAvailableRefTypesByScopeRequest } from '../reference/actions';
// helpers/constants
import * as G from '../../helpers';
import * as GC from '../../constants';
// report-common
import { getReportSagas } from '../../report-common';
// sagas
import { visitPageSaga } from '../../sagas';
// utilities
import { sendRequest } from '../../utilities/http';
import endpointsMap from '../../utilities/endpoints';
// feature clo-items
import * as A from './actions';
import {
  makeSelectUsedReport,
  makeSelectPagination,
  makeSelectPageVisited,
  makeSelectTitleSortValues,
  makeSelectAvailableReports,
  makeSelectTableTitleFilters,
} from './selectors';
//////////////////////////////////////////////////

export function* handleGetItemListSaga({ payload = {} } = {}) {
  try {
    if (G.isTrue(G.getPropFromObject('openLoader', payload))) {
      yield put(openLoader({ showDimmer: true }));
    }

    yield put(A.setListLoading(true));

    const reportParams = yield select(makeSelectUsedReport());
    const availableReports = yield select(makeSelectAvailableReports());

    if (R.and(
      G.isNilOrEmpty(availableReports),
      G.isNilOrEmpty(G.getPropFromObject('fields', reportParams)),
    )) {
      yield put(A.setListLoading(false));

      return yield put(closeLoader());
    }

    const pagination = yield select(makeSelectPagination());
    const titleOrderFields = yield select(makeSelectTitleSortValues());
    const titleFilterParams = yield select(makeSelectTableTitleFilters());
    const currentBranchGuid = yield select(makeSelectCurrentBranchGuid());

    const orderFields = G.ifElse(
      G.isNotEmpty(titleOrderFields),
      R.values(titleOrderFields),
      G.getOrElse(reportParams, 'orderFields', []),
    );

    const searchCriteria = G.getSearchCriteriaWithTitleFilters(reportParams, titleFilterParams);

    const fields = G.addRequiredFields(
      G.getOrElse(reportParams, 'fields', []),
      [GC.FIELD_GUID],
    );

    const reqBody = {
      fields,
      orderFields,
      limit: pagination.limit,
      offset: pagination.offset,
      [GC.FIELD_CURRENT_BRANCH]: currentBranchGuid,
      searchCriteria: transformSearchCriteriaBeforeReportPost(searchCriteria, GC.CLO_ITEM_REPORT),
    };

    const res = yield call(sendRequest, 'post', endpointsMap.itemListByClo, { data: reqBody });

    const { data, status } = res;

    if (G.isResponseSuccess(status)) {
      yield put(A.getItemListSuccess({ data }));
    } else {
      yield call(G.handleFailResponse, res, 'handleGetItemListSaga fail');
    }
  } catch (error) {
    yield call(G.handleException, error, 'handleGetItemListSaga exception');
  } finally {
    yield put(A.setListLoading(false));
    yield put(closeLoader());
  }
}

export function* handleExportReportDataSaga({ payload }) {
  try {
    yield put(openLoader({ showDimmer: true }));

    const currentBranchGuid = yield select(makeSelectCurrentBranchGuid());
    const searchCriteria = transformSearchCriteriaBeforeReportPost(payload.searchCriteria, GC.CLO_ITEM_REPORT);

    const { fields, fileType, orderFields } = payload;

    const reqBody = {
      fields,
      orderFields,
      searchCriteria,
      [GC.FIELD_CURRENT_BRANCH]: currentBranchGuid,
      [GC.FIELD_REPORT_NAME]: G.getPropFromObject(GC.FIELD_NAME, payload),
    };

    const options = {
      data: reqBody,
      resType: 'arraybuffer',
      params: { format: fileType },
    };

    const res = yield call(sendRequest, 'post', endpointsMap.itemListByCloExport, options);

    const { status } = res;

    if (G.isResponseSuccess(status)) {
      yield call(G.showToastrMessage, 'info', 'messages:downloading-file');
    } else {
      yield call(G.handleFailResponse, res, 'handleExportReportDataSaga fail');
    }
  } catch (error) {
    yield call(G.handleException, error, 'handleExportReportDataSaga exception');
    yield call(G.showToastrMessage, 'error', 'messages:error:unknown');
  } finally {
    yield put(closeLoader());
  }
}

const createUpdateReportSuccessCallback = (data) => G.getReportSortedBySeqFreez(data);

const {
  handleAvailableReportsRequest,
  handleCreateReportRequestSaga,
  handleUpdateReportRequestSaga,
  handleChangeDefaultReportSaga,
} = getReportSagas(GC.CLO_ITEM_REPORT, A, handleGetItemListSaga, { createUpdateReportSuccessCallback });

export function* handleVisitCloItemsPageSaga({ payload }) {
  try {
    yield call(visitPageSaga, payload, GC.CHECK_VISIT_CLO_LIST_PAGE);
    yield put(openLoader({ showDimmer: true }));

    const pageVisited = yield select(makeSelectPageVisited());

    if (G.isTrue(pageVisited)) {
      yield put(A.setInitialStateOmitReport());
    } else {
      yield put(A.setInitialState());
    }

    yield put(A.setReportPending());
    yield put(A.setIgnorePromptStatus(false));
    yield put(getAllAvailableRefTypesByScopeRequest(GC.REF_SCOPE_NAME_ITEM));

    yield call(handleAvailableReportsRequest, { notSetUsedReport: pageVisited });
    yield call(handleGetItemListSaga, { payload: { openLoader: true } });
  } catch (error) {
    yield call(G.handleException, error, 'handleVisitCloItemsPageSaga exception');
  } finally {
    yield put(closeLoader());
  }
}

function* cloItemsWatcherSaga() {
  yield takeLatest(A.getItemListRequest, handleGetItemListSaga);
  yield takeLatest(A.createReportRequest, handleCreateReportRequestSaga);
  yield takeLatest(A.updateReportRequest, handleUpdateReportRequestSaga);
  yield takeLatest(A.exportReportDataRequest, handleExportReportDataSaga);
  yield takeLatest(GC.VISIT_CLO_ITEM_LIST_PAGE, handleVisitCloItemsPageSaga);
  yield takeLatest(A.changeDefaultReportRequest, handleChangeDefaultReportSaga);
}

export default cloItemsWatcherSaga;
